import React, { useEffect, useMemo, useState } from "react";
import {
    Button,
    TextInput,
    Select,
    Group,
    Box,
    Badge,
    ActionIcon,
    Title,
} from "@mantine/core";
import { Modal as RBModal } from "react-bootstrap";
import { IconEdit, IconTrash, IconPlus } from "@tabler/icons-react";
import CommonTable from "../components/CommonTable";
import { useMediaQuery } from "@mantine/hooks";
const BASE =
    process.env.REACT_APP_API_URL || "https://news-portal-l6pf.onrender.com";
const API_URL = `${BASE}/api/auth/users`;

const User = () => {

    const [users, setUsers] = useState([]);
    const [opened, setOpened] = useState(false);
    const [editId, setEditId] = useState(null);

    // ✅ MUST BE HERE (top level inside component)
    const isMobile = useMediaQuery("(max-width: 768px)");

    const [formData, setFormData] = useState({
        username: "",
        fullName: "",
        email: "",
        phone: "",
        password: "",
        role: "reporter",
        dateOfBirth: "",
        gender: "",
    });

  
    const fetchUsers = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(API_URL, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: token ? `Bearer ${token}` : undefined,
                },
            });
            const data = await res.json();
            setUsers(data?.data || []);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const openModal = (user = null) => {
        if (user) {
            setEditId(user._id);
            setFormData({
                username: user.username || "",
                fullName: user.fullName || "",
                email: user.email || "",
                phone: user.phone || "",
                password: "",
                role: user.role || "reporter",
                dateOfBirth: user.dateOfBirth ? user.dateOfBirth.substring(0, 10) : "",
                gender: user.gender || "",
            });
        } else {
            setEditId(null);
            setFormData({
                username: "",
                fullName: "",
                email: "",
                phone: "",
                password: "",
                role: "reporter",
                dateOfBirth: "",
                gender: "",
            });
        }
        setOpened(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const token = localStorage.getItem("token");

        try {
            const method = editId ? "PUT" : "POST";
            const url = editId ? `${API_URL}/${editId}` : API_URL;

            const payload = { ...formData };
            if (editId && !payload.password) {
                delete payload.password;
            }

            const res = await fetch(url, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.message || "Something went wrong");
            }

            fetchUsers();
            setOpened(false);

            alert(data.message);
        } catch (error) {
            alert(error.message);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this user?")) return;
        const token = localStorage.getItem("token");
        await fetch(`${API_URL}/${id}`, {
            method: "DELETE",
            headers: {
                Authorization: token ? `Bearer ${token}` : undefined,
            },
        });
        fetchUsers();
    };

    const columns = useMemo(
        () => [
            { accessorKey: "username", header: "Username", size: 160 },
            { accessorKey: "fullName", header: "Name", size: 160 },
            { accessorKey: "email", header: "Email", size: 220 },
            { accessorKey: "phone", header: "Phone", size: 130 },
            {
                accessorKey: "role",
                header: "Role",
                size: 120,
                Cell: ({ cell }) => (
                    <Badge variant="light" color="blue">
                        {cell.getValue()}
                    </Badge>
                ),
            },
            {
                accessorKey: "dateOfBirth",
                header: "Date of Birth",
                size: 150,
                Cell: ({ cell }) => {
                    const val = cell.getValue();
                    if (!val) return "-";
                    return new Date(val).toLocaleDateString("en-IN");
                },
            },
            {
                accessorKey: "gender",
                header: "Gender",
                size: 120,
                Cell: ({ cell }) => {
                    const val = cell.getValue();
                    const colorMap = {
                        male: "blue",
                        female: "pink",
                        other: "gray",
                    };
                    return val ? (
                        <Badge color={colorMap[val] || "gray"} variant="light">
                            {val.charAt(0).toUpperCase() + val.slice(1)}
                        </Badge>
                    ) : "-";
                },
            },
            {
                header: "Actions",
                accessorKey: "Actions",
                size: 130,
                Cell: ({ row }) => (
                    <Group gap="xs">
                        <ActionIcon
                            variant="light"
                            color="blue"
                            onClick={() => openModal(row.original)}
                        >
                            <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon
                            variant="light"
                            color="red"
                            onClick={() => handleDelete(row.original._id)}
                        >
                            <IconTrash size={16} />
                        </ActionIcon>
                    </Group>
                ),
            },
        ],
        []
    );

    return (
        <Box p="lg">
            <Group justify="space-between" mb="lg">
                <Title order={3}>User Management</Title>
                <Button
                    leftSection={<IconPlus size={16} />}
                    onClick={() => openModal()}
                >
                    Add User
                </Button>
            </Group>

            <CommonTable columns={columns} data={users} fileName="news-users" />

            <RBModal
                show={opened}
                onHide={() => setOpened(false)}
                centered
                fullscreen="sm-down"
            >
                <RBModal.Header closeButton>
                    <RBModal.Title>{editId ? "Edit User" : "Add User"}</RBModal.Title>
                </RBModal.Header>
                <RBModal.Body>
                <form onSubmit={handleSubmit}>
                    <TextInput
                        label="Username"
                        required
                        mb="md"
                        value={formData.username}
                        onChange={(e) =>
                            setFormData({ ...formData, username: e.target.value })
                        }
                    />

                    <TextInput
                        label="Full Name"
                        required
                        mb="md"
                        value={formData.fullName}
                        onChange={(e) =>
                            setFormData({ ...formData, fullName: e.target.value })
                        }
                    />

                    <TextInput
                        label="Email"
                        required
                        mb="md"
                        value={formData.email}
                        onChange={(e) =>
                            setFormData({ ...formData, email: e.target.value })
                        }
                    />

                    <TextInput
                        label="Password"
                        type="password"
                        required={!editId}
                        mb="md"
                        placeholder={
                            editId
                                ? "Leave blank to keep current password"
                                : "Enter password"
                        }
                        value={formData.password}
                        onChange={(e) =>
                            setFormData({ ...formData, password: e.target.value })
                        }
                    />

                    <TextInput
                        label="Phone"
                        mb="md"
                        value={formData.phone}
                        onChange={(e) =>
                            setFormData({ ...formData, phone: e.target.value })
                        }
                    />

                    <Select
                        label="Role"
                        mb="md"
                        data={[{ value: "reporter", label: "Reporter" }]}
                        value={formData.role}
                        onChange={(value) =>
                            setFormData({ ...formData, role: value })
                        }
                    />

                    <TextInput
                        label="Date of Birth"
                        type="date"
                        mb="md"
                        value={formData.dateOfBirth}
                        onChange={(e) =>
                            setFormData({ ...formData, dateOfBirth: e.target.value })
                        }
                    />

                    <Select
                        label="Gender"
                        mb="lg"
                        placeholder="Select gender"
                        data={[
                            { value: "male", label: "Male" },
                            { value: "female", label: "Female" },
                            { value: "other", label: "Other" },
                        ]}
                        value={formData.gender}
                        onChange={(value) =>
                            setFormData({ ...formData, gender: value })
                        }
                    />

                    <Group justify={isMobile ? "center" : "flex-end"}>
                        <Button type="submit" fullWidth={isMobile}>
                            {editId ? "Update User" : "Create User"}
                        </Button>
                    </Group>
                </form>
                </RBModal.Body>
            </RBModal>
        </Box>
    );
};

export default User;